import React, { useRef } from 'react'
import { RouteComponentProps } from 'react-router'
import { ISelecetedFilters, ISummaryData } from '../../../utility/types'
import { IPageRenderConfig, IDateRange, IKeyValue, IApiFilter } from '@fareye/types'
import { useTranslation, zonedToUTC, zonedToUTCAdd1Day, downloadToCSV } from '@fareye/common'
import { cancelToken, camelCase, snakeCase, HEADERS, post, isEmpty, serialzeAggregationsToSummaryData } from '@fareye/utils'
import queryString from 'query-string'
import { getTableColumns } from './Column'
import { Flex, RenderControl, Table, styled, Button, dataPointWidth } from '@fareye/ui'

interface ITable extends RouteComponentProps {
  config: IPageRenderConfig,
  pageType: IKeyValue<string>,
  setSummaryData: React.Dispatch<React.SetStateAction<ISummaryData>>,
  selectedFilters: ISelecetedFilters,
  timer: number | React.Dispatch<React.SetStateAction<number>> | null
}

const MultiModalTableWrapper = styled(Flex)`
  background: #FFFFFF;
  border-radius: 12px;
  padding: 10px;
`

const StyledTable = styled(Table)`
  table {
      border: 0;
      thead
      tr {
        background: #fff;
        border-bottom: 1px solid #E2E4E6;
      }
      th > .thead-flex {
      padding-right: 6px;
      border-right: 1px solid #E2E4E6;
      }
      th, td {
        background: #fff;
        border-right: none;
      }
      td {
        border: 0;
        overflow: hidden;
        background: #fff;
        border-bottom: 1px solid #E2E4E6;
      }
    }
  }

  ${(props: any) => props.verticalAlign ? `table{td{vertical-align:text-top}}` : ``}
  ${(props: any) => props.padding ? `table{td{padding:${props.padding}}}` : ``}
`

const initApiResponse = {
  data: [],
  isLoading: false,
  isError: false,
  isSuccess: true,
  message: '',
  meta: {
    count: -1,
    pageNumber: 1
  }
}

const MultiModalTable: React.FC<ITable> = React.memo((props) => {
  const { config, timer, setSummaryData, selectedFilters, pageType } = props
  const [apiData, setApiResponse] = React.useState<any>(initApiResponse)
  const [sorting, setSortBy] = React.useState({
    sortBy: '',
    sortByDirection: true
  })
  const { t } = useTranslation()
  const source = cancelToken().source()
  const [changeDetect, setChangeDetect] = React.useState(false)
  const [pageNo, setPageNo] = React.useState(0)
  const [recordsPerPage, setRecordsPerPage] = React.useState(10)

  const { style, className, variant } = config.styling
  const query = queryString.parse(location.search)
  const {
    links,
    apiConfig
  } = config.config

  const selectedColumn = React.useMemo(() => {
    const columns = config.config.columns as any
    const sortableColumns = apiData?.meta?.sortableAttributes || []

    return getTableColumns(columns, {
      t,
      links: config.config.links,
      queryParams: {}
    })
      .filter((value: any, index: number, arr: any) => {
        // removes duplicate column with same id
        return arr.findIndex((element: any) => element.id == value.id) === index
      })
      .map((item: any) => ({
        ...item,
        sortable: sortableColumns.includes(
          item.id
            .split('.')
            .map((item: string) => camelCase(item))
            .join('.')
        )
      }))
  },[])

  const isFirstRun = useRef(false)
  React.useEffect(() => {
    if (isFirstRun.current) {
      setPageNo(0)
      setApiResponse(initApiResponse)
      setChangeDetect(!changeDetect)
    }
    isFirstRun.current = true
  }, [recordsPerPage, selectedFilters])

  React.useEffect(() => {
    fetchData()
  }, [pageNo, changeDetect, sorting, timer])

  function generateSortingObj() {
    const { sortByDirection, sortBy } = sorting
    if (sortBy) {
      const snakeParams = sortBy
        .split('.')
        .map(i => snakeCase(i))
        .join('.')
      const sort = [
        { sortBy: snakeParams, sortDir: sortByDirection ? 'asc' : 'desc' }
      ]
      return { sort }
    } else {
      return { sort: [{ sortBy: 'exp_start_time_utc', sortDir: 'desc' }] }
    }
  }

  function generateFilterData() {
    let filter: any[] = []
    Object.entries(selectedFilters).forEach(([filterType, filters]) => {
      switch(filterType) {
        case("dateRange"):
          Object.entries(filters).forEach(([filterKey, filterValue]) => {
            const { startDate, endDate } = filterValue
            const startFilter = [
              {
                filterKey: filterKey,
                filterValue: startDate ? zonedToUTC(startDate) : new Date(),
                condition: 'include',
                filterType: 'gte'
              }
            ]
            const endFilter = [
              {
                filterKey: filterKey,
                filterValue: endDate ? zonedToUTCAdd1Day(endDate) : new Date(),
                condition: 'include',
                filterType: 'lt'
              }
            ]
            filter.push(startFilter, endFilter)
          })
          break
        case("multiSelect"):
        default:
          Object.entries(filters).forEach(([filterKey, filterValue]) => {
            if (filterValue.length > 0) {
              filter.push([
                {
                  filterKey: filterKey,
                  filterValue: filterValue.map((value: any) => getFilterValue(filterKey, value)),
                  condition: 'include',
                  filterType: 'eq'
                }
              ])
            }
          })
          break
      }
    })
    return filter
  }
  
  function getFilterValue(filterKey: string, filterValue: any) {
    switch(filterKey) {
      case("is_active"):
        return !!filterValue
      default:
        return filterValue
    }
  }
  
  async function fetchData() {
    try {
      setApiResponse({
        ...apiData,
        isLoading: true
      })
      const { apiURL, queryParams, aggKeys } = apiConfig as IApiFilter
      let filter = Object.values(queryParams as IKeyValue<any>).map((value: any) => [value])
      filter = [...filter, ...generateFilterData()]
      const {
        data: { results, meta, aggregations }
      } = await post(
        apiURL,
        {
          filter,
          perPage: recordsPerPage,
          page: pageNo + 1,
          aggs: aggKeys,
          defaultAggs: false,
          ...generateSortingObj()
        },
        source.token,
        {
          headers: HEADERS()['HEADER_INFLECTION']
        }
      )
      // merge the data with previous state on the page index
      // of which data is fetched of page size assuming pageindex starts from 0
      // ex pageIndex = 3 pageSize = 10 t.splice(30, 10, ...data)
      // this implemetation covers empty array refresh of same pageIndex
      // const tempData = results
      const tempData = [...apiData.data]
      tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      // tempData.splice(pageNo * recordsPerPage, recordsPerPage, ...results)
      if (results) {
        setApiResponse({
          ...apiData,
          data: tempData,
          isSuccess: true,
          meta: {
            ...meta,
            sortableAttributes: meta.sortableAttributes.map((column: string) =>
              column
                .split('.')
                .map((item: string) => camelCase(item))
                .join('.')
            )
          },
          isError: false,
          message: isEmpty(results) ? 'No Data Found...' : '',
          isLoading: false
        })
        setSummaryData(serialzeAggregationsToSummaryData(aggregations))
      }
    } catch (ex:any) {
      setApiResponse({
        data: [],
        isSuccess: false,
        isError: true,
        message: t('Something went wrong...'),
        isLoading: false
      })
    }
  }
  
  return (
    <RenderControl state={apiData} variant={'m'} t={t}>
      <Flex style={{ justifyContent: 'flex-end', marginBottom: '10px' }}>
      <Button
        variant="s primary"
        style={{ height: '30px' }}
        onClick={() => {
          const columns = config?.config?.columns ?? []; 
          const headers = columns.map((col: any) => col.label);
          let keys = columns.map((col: any) => col.key);

          if (keys.includes('currentTemperature')) {
            keys = keys.map(col =>
              col === 'currentTemperature' ? 'deviceInfo.extraData.temperature' : col
            );
          }

          function getNestedValue(obj: any, path: string): any {
            return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), obj);
          }

          const formattedData = apiData.data.map((row: Record<string, any>) =>
            Object.fromEntries(keys.map((key: string, index: number) => [headers[index], getNestedValue(row, key)]))
          );

          downloadToCSV({
            data: formattedData,
            fileName: `temperatureData_${new Date().toISOString().slice(0, 10)}`
          });
        }}
         >
        {t('Download CSV')}
        </Button>
      </Flex>
      <MultiModalTableWrapper style={{...style}}>
        <StyledTable
          verticalAlign={true}
          className={`responsive  tabulare ${className} multi-model`}
          data={apiData.data}
          padding="4px 6px"
          size="s"
          showTotalCount={false}
          sorting={sorting}
          sortable={true}
          totalElements={apiData?.meta?.count || apiData.data.length}
          columns={selectedColumn}
          selectedFilters={selectedFilters}
          t={t}
          onSortedChange={(ev: any) => {
            setSortBy({
              sortBy: ev[0].id,
              sortByDirection:
                ev[0].id === sorting.sortBy
                  ? !sorting.sortByDirection
                  : !ev[0].desc
            })
          }}
          defaultPageSize={recordsPerPage}
          onPageSizeChange={(pageSize: number) => {
            setPageNo(0)
            setRecordsPerPage(pageSize)
          }}
          defaultPageIndex={pageNo}
          onPageChange={(pageIndex: number) => {
            setPageNo(pageIndex)
          }}
        />
      </MultiModalTableWrapper>
    </RenderControl>
  )
})

export default MultiModalTable